/**
 * persistentToastNotification.js
 */


import React from 'react';
import { Button, Card, CardActions, Typography, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useSnackbar } from 'notistack';
import ConfirmDialog from 'components/confirm-dialog';
import DateDistance from 'components/date-distance';
import {
  NotificationItemParseJsxStringAndGenWithLinks
} from './index';
import Icon from 'components/icon';


const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    backgroundColor: theme.palette.comp.notifications.toastBackground,
    color: theme.palette.sys.onPrimary,
    padding: theme.spacing(2),
    boxShadow: theme.boxShadowMaker(...theme.palette.comp.notifications.toastShadowParams),
    [theme.breakpoints.down('sm')]: {
      padding: `${theme.spacing(1.5)} ${theme.spacing(1.5)} ${theme.spacing(2)} ${theme.spacing(2.5)}`,
    },
    '& .MuiCardActions-root' : {
      flexDirection: 'column',
      alignItems: 'flex-end',
    },
  },
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
    [theme.breakpoints.down('sm')]: {
    },
  },
  titleContainer: {
    display: 'flex',
    flexGrow: 2,
  },
  title: {
    fontWeight: 600,
    lineHeight: 1.2,
    textAlign: 'left',
    flexGrow: 2,

    [theme.breakpoints.down('sm')]: {
      fontSize: theme.typography.sizes[3],
    },
  },
  xButton: {
    minWidth: 44,
    minHeight: 44,
    marginRight: 0,
    marginLeft: theme.spacing(1),
    padding: `${theme.spacing(.75)} 0`,

    '& ~ &': {
      //marginLeft: theme.spacing(2),
    },
  },
  closeIcon: {
    fontSize: '2rem',
  },
  infoIcon: {
    marginRight: theme.spacing(2),
    fontSize: '2rem',

    [theme.breakpoints.down('sm')]: {
      marginRight: theme.spacing(1),
      fontSize: '1.25rem',
    },
  },
  messageContainer: {
    maxWidth: 356,

    [theme.breakpoints.down('sm')]: {
      maxWidth: 'inherit',
      paddingRight: theme.spacing(2.5),
    },
  },
  message: {
    textAlign: 'left',
  },
  messageLink: {
    color: theme.palette.sys.secondaryContainer,
    fontWeight: 600,
    textTransform: 'uppercase',
    '&:hover': {
      color: theme.palette.sys.secondary
    }
  },
  dateDistance: {
    display: 'inline-block',
    fontSize: theme.typography.sizes[2],
    marginLeft: theme.spacing(2),
    whiteSpace: 'nowrap',
  },
  mobileDismissButtons: {
    whiteSpace: 'nowrap',
  },
  dismissButtonRoot: {
    color: 'inherit',
    borderColor: 'inherit',
    marginLeft: theme.spacing(2),
    whiteSpace: 'nowrap',

    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.1)'
    },

    '& ~ &': {
      marginTop: theme.spacing(2),
    },
  },
}));


const NotificationTitle = (props) => {
  const {
    id,
    title,
    classes,
    onDismiss,
    onDismissAll,
    unreadCount,
    isMobile,
  } = props;

  return <>
    <div className={classes.titleRow}>
      <div className={classes.titleContainer}>
        {isMobile && <Icon type='info' className={classes.infoIcon} />}
        <Typography variant="h6" className={classes.title}>
          {title}
        </Typography>
      </div>      
      {isMobile && <div className={classes.mobileDismissButtons}>
        {unreadCount > 1 &&
          <Button
            classes={{ root: classes.xButton }}
            title="Mark all as read"
            aria-label="Mark all as read"
            color='inherit'
            onClick={onDismissAll}
          >
            <Icon type='check-all' className={classes.closeIcon} />
          </Button>          
        }      
        <Button
          classes={{ root: classes.xButton }}
          title="Mark as read"
          aria-label="Mark as read"
          color='inherit'
          onClick={() => onDismiss(id)}
        >
          <Icon type='checkmark' className={classes.closeIcon} />
        </Button>
      </div>}
    </div>
  </>
};


export const PersistentToastNotification = React.forwardRef((props, ref) => {
  const {
    id,
    notification,
    onDismiss: onDismissAdded,
    onDismissAll: dismissAll,
    unreadCount,
  } = props;
  const classes = useStyles();
  const theme = useTheme();

  const { closeSnackbar } = useSnackbar();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [confirmDismissAll, setConfirmDismissAll] = React.useState();


  const onDismiss = (dismissKey) => {
    closeSnackbar(dismissKey);
    onDismissAdded(dismissKey);
  };


  const onDismissAll = async () => {
    const confirmed = await confirmDismissAll();
    if (confirmed) {
      dismissAll();
    }
  };

  const notificationTitle = (
    <NotificationTitle 
      title={notification.title} 
      id={id} 
      classes={classes} 
      onDismiss={onDismiss} 
      onDismissAll={onDismissAll} 
      unreadCount={unreadCount} 
      isMobile={isMobile} 
    />
  );

  return <>
    <Card
      id='notistack-snackbar'
      data-testid='persistent-toast-notification'
      className={classes.container}

      // TODO: remove special case (or generalize)
      sx={notification.title === 'IMPORTANT! File Recovery Notice' ? {
        backgroundColor: 'hsl(29, 80%, 44%) !important',
        color: '#fff !important', 
      } : undefined}

      ref={ref}
    >
      <div className={classes.iconContainer}>
        {!isMobile && <Icon type='info' className={classes.infoIcon} />}
      </div>
      <div className='MuiSnackbarContent-message'>
        {isMobile && notificationTitle}
        <div className={classes.middleColumnContainer}>
          {!isMobile && notificationTitle}
          <div className={classes.messageContainer}>
            {/* <div className={classes.headerWrapper}>
            <NotificationItemChip notification={notification} />
            </div> */}
            <Typography variant="body1" className={classes.message}>
              <NotificationItemParseJsxStringAndGenWithLinks
                jsxString={notification.message}
                linkClass={classes.messageLink}
                doRemoveImgs={isMobile}
                onLink={() => onDismiss(id)}
              />
              <DateDistance
                dateStart={props.notification.date}
                cozyPrefix="– "
                suffix="ago"
                showTime={true}
                component={'span'}
                componentProps={{ display: 'inline', className: classes.dateDistance }}
              />
            </Typography>
          </div>
        </div>
      </div>
      {!isMobile &&
        <CardActions>
          <Button
            classes={{ root: classes.dismissButtonRoot }}
            title="Mark as read"
            aria-label="Mark as read"
            variant="outlined"
            onClick={() => onDismiss(id)}
            startIcon={<Icon type='checkmark' />}
          >
            DISMISS
          </Button>
          {unreadCount > 1 &&
            <Button
              classes={{ root: classes.dismissButtonRoot }}
              title="Mark all as read"
              aria-label="Mark all as read"
              onClick={onDismissAll}
              startIcon={<Icon type='check-all' />}
            >
              DISMISS ALL
            </Button>          
          }
        </CardActions>
      }

      {/* dismiss all notifications confirmation dialog */}
      <ConfirmDialog
        title="Dismiss All Notifications"
        titleStyle="warning"
        titleIcon="warning"
        content={<>
          This will dismiss {unreadCount} active notifications. 
          Review them later by tapping
          {isMobile
            ? ' the main menu (located in the upper left) and then selecting the Notifications option'
            : ' on the bell icon in the title bar at the top right of the page'
          }.
        </>}
        setConfirm={setConfirmDismissAll}
      />     

    </Card>
  </>
});